import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, Send, Sparkles, Flame, Plus, X, Wand2, Play } from "lucide-react";
import AIDraftEmailDialog from "@/components/AIDraftEmailDialog";
import MentionTextarea from "@/components/MentionTextarea";
import { useTimer } from "@/context/TimerContext";

const STATUS_OPTIONS = ["lead", "prospect", "customer", "archived"];
const ACTIVITY_TYPES = [
  { key: "note", label: "Note" },
  { key: "email", label: "Email (mock)" },
  { key: "sms", label: "SMS (mock)" },
  { key: "call", label: "Call log" },
];

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [activities, setActivities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [actType, setActType] = useState("note");
  const [actContent, setActContent] = useState("");
  const [newCfKey, setNewCfKey] = useState("");
  const [newCfVal, setNewCfVal] = useState("");
  const [draftOpen, setDraftOpen] = useState(false);
  const { start: startTimer, active: activeTimer } = useTimer();

  const startTimerForContact = async () => {
    const task = window.prompt("Task (optional):", "");
    if (task === null) return;
    await startTimer({
      contact_id: id,
      task: (task || "").trim(),
      hourly_rate: contact?.hourly_rate ?? null,
    });
  };

  const load = async () => {
    try {
      const [{ data: c }, { data: a }] = await Promise.all([
        api.get(`/contacts/${id}`),
        api.get(`/contacts/${id}/activities`),
      ]);
      setContact({ ...c, tags: (c.tags || []).join(", "), custom_fields: c.custom_fields || {} });
      setActivities(a);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      navigate("/contacts");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...contact, tags: contact.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      await api.put(`/contacts/${id}`, payload);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const rescore = async () => {
    setScoring(true);
    try {
      const { data } = await api.post(`/contacts/${id}/score`);
      setContact({ ...data, tags: (data.tags || []).join(", "), custom_fields: data.custom_fields || {} });
      toast.success(`Scored: ${data.score}/100`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Scoring failed");
    } finally { setScoring(false); }
  };

  const addCustomField = () => {
    if (!newCfKey.trim()) return;
    setContact({ ...contact, custom_fields: { ...(contact.custom_fields || {}), [newCfKey.trim()]: newCfVal } });
    setNewCfKey(""); setNewCfVal("");
  };
  const removeCustomField = (k) => {
    const cf = { ...(contact.custom_fields || {}) };
    delete cf[k];
    setContact({ ...contact, custom_fields: cf });
  };
  const setCustomField = (k, v) => setContact({ ...contact, custom_fields: { ...(contact.custom_fields || {}), [k]: v } });

  const remove = async () => {
    if (!confirm("Delete this contact?")) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Deleted");
      navigate("/contacts");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const logActivity = async (e) => {
    e.preventDefault();
    if (!actContent.trim()) return;
    try {
      const { data } = await api.post(`/contacts/${id}/activities`, { type: actType, content: actContent });
      setActivities([data, ...activities]);
      setActContent("");
      const sent = /\[SENT /.test(data.content || "");
      if (actType === "email") toast.success(sent ? "Email sent via Resend" : "Email logged (Resend not configured or send failed)");
      else if (actType === "sms") toast.success(sent ? "SMS sent via Twilio" : "SMS logged (Twilio not configured or send failed)");
      else toast.success("Logged");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!contact) return <div className="p-10 section-label">Loading…</div>;

  return (
    <div className="p-6 md:p-10" data-testid="contact-detail-page">
      <Link to="/contacts" data-testid="back-contacts" className="section-label inline-flex items-center gap-1 hover:text-[var(--peak-accent)]">
        <ArrowLeft className="w-3 h-3" /> Back to contacts
      </Link>

      <div className="flex items-end justify-between mt-2 mb-8 flex-wrap gap-4">
        <div>
          <div className="section-label">Contact</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase mt-1">
            {(contact.first_name || "") + " " + (contact.last_name || "") || "Unnamed"}
          </h1>
          <div className="text-[var(--peak-muted)] mt-1 text-sm">{contact.email || contact.phone || "—"}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={startTimerForContact}
            data-testid="start-timer-for-contact"
            disabled={!!activeTimer}
            title={activeTimer ? "Stop the current timer first" : `Start timer for this contact${contact?.hourly_rate ? ` at $${contact.hourly_rate}/hr` : ""}`}
            className="border-2 border-[#008A27] text-[#008A27] hover:bg-[#008A27] hover:text-white font-bold uppercase tracking-widest px-4 py-3 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Play className="w-4 h-4" /> Start timer
          </button>
          <button onClick={() => setDraftOpen(true)} data-testid="ai-draft-email-btn" className="bg-[var(--peak-blue-deep)] hover:bg-black text-white font-bold uppercase tracking-widest px-4 py-3 inline-flex items-center gap-2">
            <Wand2 className="w-4 h-4" /> AI draft email
          </button>
          <button onClick={save} data-testid="save-contact" disabled={saving} className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={remove} data-testid="delete-contact" className="border-2 border-[var(--peak-ink)] hover:bg-[#E50000] hover:border-[#E50000] hover:text-white text-[var(--peak-ink)] font-bold uppercase tracking-widest px-6 py-3 inline-flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 brutalist-card p-6">
          <h2 className="font-heading font-bold text-xl tracking-tight mb-4">Details</h2>
          <div className="grid grid-cols-2 gap-4">
            {[["first_name", "First name"], ["last_name", "Last name"], ["email", "Email"], ["phone", "Phone"], ["company", "Company"], ["source", "Source"]].map(([k, l]) => (
              <div key={k} className={k === "email" || k === "company" ? "col-span-2" : ""}>
                <label className="section-label">{l}</label>
                <input
                  data-testid={`field-${k}`}
                  className="brutalist-input"
                  value={contact[k] || ""}
                  onChange={(e) => setContact({ ...contact, [k]: e.target.value })}
                />
              </div>
            ))}
            <div>
              <label className="section-label">Status</label>
              <select
                data-testid="field-status"
                className="brutalist-input"
                value={contact.status}
                onChange={(e) => setContact({ ...contact, status: e.target.value })}
              >
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="section-label">Tags (comma-separated)</label>
              <input
                data-testid="field-tags"
                className="brutalist-input"
                value={contact.tags}
                onChange={(e) => setContact({ ...contact, tags: e.target.value })}
              />
            </div>
            <div>
              <label className="section-label">Hourly rate ($)</label>
              <input
                data-testid="field-hourly-rate"
                type="number" min="0" step="0.01"
                className="brutalist-input"
                value={contact.hourly_rate ?? ""}
                onChange={(e) => setContact({ ...contact, hourly_rate: e.target.value === "" ? null : parseFloat(e.target.value) })}
                placeholder="e.g. 75 (used to auto-fill timers)"
              />
            </div>
            <div className="col-span-2">
              <label className="section-label">Notes</label>
              <textarea
                data-testid="field-notes"
                rows={4}
                className="w-full border border-[var(--peak-border)] p-2 focus:outline-none focus:border-[var(--peak-ink)]"
                value={contact.notes || ""}
                onChange={(e) => setContact({ ...contact, notes: e.target.value })}
              />
            </div>
          </div>

          {/* Custom fields */}
          <div className="mt-6 pt-6 border-t border-[var(--peak-border)]" data-testid="custom-fields-section">
            <div className="section-label mb-3">Custom fields</div>
            <div className="space-y-2">
              {Object.entries(contact.custom_fields || {}).map(([k, v]) => (
                <div key={k} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center" data-testid={`cf-row-${k}`}>
                  <div className="text-xs font-mono uppercase tracking-wide text-[var(--peak-muted)]">{k}</div>
                  <input className="brutalist-input" value={v} onChange={(e) => setCustomField(k, e.target.value)} data-testid={`cf-value-${k}`} />
                  <button onClick={() => removeCustomField(k)} data-testid={`cf-remove-${k}`} className="hover:text-[#E50000]"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <div className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center pt-2">
                <input className="brutalist-input" placeholder="field name" value={newCfKey} onChange={(e) => setNewCfKey(e.target.value)} data-testid="cf-new-key" />
                <input className="brutalist-input" placeholder="value" value={newCfVal} onChange={(e) => setNewCfVal(e.target.value)} data-testid="cf-new-value" />
                <button onClick={addCustomField} data-testid="cf-add" className="p-2 bg-[var(--peak-ink)] text-white hover:bg-[var(--peak-accent)]"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        </div>

        {/* Right column: AI score + Activity */}
        <div className="flex flex-col gap-4">
          <div className="brutalist-card p-6" data-testid="ai-score-card">
            <div className="flex items-center justify-between">
              <div className="section-label flex items-center gap-1"><Sparkles className="w-3 h-3 text-[var(--peak-blue-deep)]" /> AI lead score</div>
              <button
                onClick={rescore}
                disabled={scoring}
                data-testid="rescore-btn"
                className="text-xs font-bold uppercase tracking-widest text-[var(--peak-blue-deep)] hover:text-[var(--peak-accent)] disabled:opacity-60"
              >
                {scoring ? "Scoring…" : "Rescore"}
              </button>
            </div>
            {typeof contact.score === "number" ? (
              <>
                <div className="mt-3 flex items-end gap-2">
                  {contact.score >= 70 && <Flame className="w-8 h-8 text-[var(--peak-accent)]" />}
                  <div className={`font-heading font-black text-6xl tracking-tighter ${contact.score >= 70 ? "text-[var(--peak-accent)]" : contact.score >= 40 ? "text-[var(--peak-ink)]" : "text-[var(--peak-muted)]"}`} data-testid="score-value">
                    {contact.score}
                  </div>
                  <div className="section-label mb-2">/ 100</div>
                </div>
                {contact.score_reasoning && (
                  <div className="mt-3 text-sm text-[var(--peak-ink)] border-l-2 border-[var(--peak-blue-deep)] pl-3" data-testid="score-reasoning">{contact.score_reasoning}</div>
                )}
              </>
            ) : (
              <div className="mt-3 text-sm text-[var(--peak-muted)]">Not scored yet. Click "Rescore" to have the AI grade this lead based on details and activity.</div>
            )}
          </div>

          <div className="brutalist-card p-6 flex flex-col flex-1">
          <h2 className="font-heading font-bold text-xl tracking-tight mb-4">Activity</h2>
          <form onSubmit={logActivity} className="border border-[var(--peak-border)] p-3 mb-4">
            <div className="flex gap-1 mb-2">
              {ACTIVITY_TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`activity-type-${t.key}`}
                  onClick={() => setActType(t.key)}
                  className={`px-2 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${
                    actType === t.key ? "bg-[var(--peak-ink)] text-white" : "hover:bg-[var(--peak-surface-alt)]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <MentionTextarea
              testId="activity-content"
              rows={2}
              className="w-full border border-[var(--peak-border)] p-2 text-sm focus:outline-none focus:border-[var(--peak-ink)]"
              value={actContent}
              onChange={(e) => setActContent(e.target.value)}
              placeholder={
                actType === "email"
                  ? "First line: 'Subject: your subject'\\nThen the body. Sent via Resend when configured."
                  : actType === "sms"
                  ? "SMS body — sent via Twilio when configured, else logged as activity."
                  : "Log a note, call, etc. Type @ to mention a teammate."
              }
            />
            <button type="submit" data-testid="submit-activity" className="mt-2 w-full bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest text-xs px-3 py-2 inline-flex items-center justify-center gap-2">
              <Send className="w-3 h-3" /> Log activity
            </button>
          </form>

          <div className="space-y-3 overflow-y-auto tick-scroll max-h-[400px]" data-testid="activity-timeline">
            {activities.length === 0 && (
              <div className="text-sm text-[var(--peak-muted)]">No activity yet.</div>
            )}
            {activities.map((a) => (
              <div key={a.id} className="border-l-2 border-[var(--peak-ink)] pl-3 py-1">
                <div className="section-label">{a.type} · {new Date(a.created_at).toLocaleString()}</div>
                <div className="text-sm mt-1 whitespace-pre-wrap">{a.content}</div>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>
      <AIDraftEmailDialog open={draftOpen} onOpenChange={setDraftOpen} contactId={id} />
    </div>
  );
}
